import { ImageIcon, Loader2, Pencil } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { TenantLogoWatermark } from '@/components/ui/tenant-logo-watermark'
import type { Organisation } from '@/hooks/use-organisations'
import { cn } from '@/lib/utils'

export interface OrganisationBannerSectionProps {
  organisation: Organisation
  bannerUrl?: string | null
  isUploadingBanner?: boolean
  onEditClick: () => void
  className?: string
}

export function OrganisationBannerEmptySection({
  onEditClick,
  className,
}: {
  onEditClick?: () => void
  className?: string
}) {
  return (
    <section className={cn('relative z-[1] overflow-visible mb-1.5', className)}>
      <div className="relative overflow-hidden rounded-xl border border-[var(--page-background)] bg-muted/40 group/banner">
        <div className="relative flex h-60 w-full shrink-0 overflow-hidden">
          <div className="relative isolate h-full w-full">
            <TenantLogoWatermark />
            <div className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center">
              <ImageIcon className="size-10 text-muted-foreground opacity-60" aria-hidden />
            </div>
          </div>
        </div>
        {onEditClick ? (
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            className="absolute right-2 top-2 z-[2] shrink-0 bg-background/80 backdrop-blur-sm opacity-100 transition-opacity hover:bg-accent md:right-4 md:top-4 md:opacity-0 md:group-hover/banner:opacity-100 focus-visible:opacity-100"
            onClick={onEditClick}
            aria-label="Create organisation"
            title="Create organisation"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
    </section>
  )
}

/** View-only employer org cover preview with pencil — editing opens OrganisationBannerCanvas. */
export function OrganisationBannerSection({
  organisation,
  bannerUrl,
  isUploadingBanner = false,
  onEditClick,
  className,
}: OrganisationBannerSectionProps) {
  return (
    <section className={cn('relative z-[1] overflow-visible mb-1.5', className)}>
      <div
        className={cn(
          'relative overflow-hidden rounded-xl border border-[var(--page-background)] bg-muted/40 group/banner'
        )}
        {...(organisation.id ? { 'data-agent-highlight': `organisation:${organisation.id}:cover` } : {})}
      >
        <div
          className={cn(
            'relative flex h-60 w-full shrink-0 overflow-hidden',
            isUploadingBanner && 'pointer-events-none'
          )}
          aria-hidden={isUploadingBanner}
        >
          {bannerUrl ? (
            <img src={bannerUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="relative isolate h-full w-full">
              <TenantLogoWatermark />
              <div className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center">
                <ImageIcon className="size-10 text-muted-foreground opacity-60" aria-hidden />
              </div>
            </div>
          )}
          {isUploadingBanner ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/70">
              <Loader2 className="size-8 animate-spin text-foreground" aria-hidden />
            </div>
          ) : null}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          className="absolute right-2 top-2 z-[2] shrink-0 bg-background/80 backdrop-blur-sm opacity-100 transition-opacity hover:bg-accent md:right-4 md:top-4 md:opacity-0 md:group-hover/banner:opacity-100 focus-visible:opacity-100"
          onClick={onEditClick}
          aria-label="Edit cover image"
          title="Edit cover image"
        >
          <Pencil className="h-4 w-4" />
        </Button>
      </div>
    </section>
  )
}
